import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import Icon from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ThemeFontLoader from "@tSurya/Components/ThemeFontLoader";
import MGBWSearchIcon from "@tSurya/Icons/MGBWSearchIcon";
import MGBWCloseIcon from "@tSurya/Icons/MGBWCloseIcon";
import { MGBWCloseIconSmall } from "@tSurya/Icons/MGBWCloseIcon";
import surHeaderHeight from "@tSurya/Handlers/SurHeaderHeightHandler";
import { COMMON, TYPOGRAPHY } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import React, { createContext, useContext, useEffect, useReducer, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface MobileSearchState {
    isVisible: boolean;
}

type MobileSearchAction =
    | { type: "TOGGLE_VISIBILITY" }
    | { type: "SET_VISIBILITY"; payload: boolean }
    | { type: "HIDE" }
    | { type: "SHOW" };

const mobileSearchReducer = (state: MobileSearchState, action: MobileSearchAction): MobileSearchState => {
    switch (action.type) {
        case "TOGGLE_VISIBILITY":
            return { ...state, isVisible: !state.isVisible };
        case "SET_VISIBILITY":
            return { ...state, isVisible: action.payload };
        case "SHOW":
            return { ...state, isVisible: true };
        case "HIDE":
            return { ...state, isVisible: false };
        default:
            return state;
    }
};

const initialMobileSearchState: MobileSearchState = {
    isVisible: false,
};

interface MobileSearchContextType {
    isVisible: boolean;
    toggleMobileSearch: () => void;
    showMobileSearch: () => void;
    hideMobileSearch: () => void;
    focusSearchInput: () => void;
}

const MobileSearchContext = createContext<MobileSearchContextType | null>(null);

export const useMobileSearch = () => {
    const context = useContext(MobileSearchContext);
    if (!context) {
        throw new Error("useMobileSearch must be used within a MobileSearchProvider");
    }
    return context;
};

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    surHeaderHeight,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    mobileSearchBar?: InjectableCss;
    searchInteractionBlocker?: InjectableCss;
    brandLogo?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    scrollFixed?: InjectableCss;
    headerWrapper?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        css: css``,
    },
    scrollFixed: {
        css: css`
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            background: #fff;
            width: 100%;
            z-index: 99;
            box-shadow: 0px 5px 10px #00000029;
            height: auto;
            transition: all 1s ease-in-out;
            + main {
                padding-top: 171.5px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            position: fixed;
                            padding-bottom: 10px;
                        `,
                        null,
                    ],
                    "max",
                )}

            ~ main[class^="PageStyle"] {
                min-height: 500px;
                padding: 15px 0;
                padding-top: 171.5px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                padding: 15px;
                            `,
                            css`
                                padding: 15px;
                            `,
                        ],
                        "max",
                    )}
            }
            ~ main[data-test-selector="homePage"] {
                padding: 0;
            }
        `,
    },
    headerWrapper: {
        css: css`
            background-color: #fdfdfd;
            height: auto;
            transition: height 1s;
            position: sticky;
            top: 0;
            letter-spacing: ${TYPOGRAPHY.header.letterSpacing};
            z-index: 1300;
            border-bottom: 1px solid #e8e8e8;

            @media (min-width: 992px) and (max-width: 1166px) {
                padding-bottom: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                        margin-bottom: 0;
                    `,
                    css`
                        padding: 0;
                        margin-bottom: 0;
                    `,
                    css`
                        padding-bottom: 0;
                    `,
                    null,
                    null,
                ])}
            & + div[class^="PageStyle"] {
                padding: 0px;
                padding-bottom: 15px;
                nav[data-test-selector="pageBreadcrumbs"] {
                    width: 100%;
                    display: flex;
                    max-width: ${COMMON.width.content}px;
                    margin: 0 auto;
                    flex-wrap: wrap;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            null,
                            null,
                            css`
                                overflow-x: auto;
                                white-space: nowrap;
                                margin: 0 auto;
                                margin-top: 8px;
                            `,
                            css`
                                overflow-x: auto;
                                white-space: nowrap;
                                margin: 0 auto;
                                margin-top: 8px;
                            `,
                        ])}
                    @media only screen and (max-width: 1280px) {
                        padding-left: 15px !important;
                        padding-right: 15px !important;
                    }
                    > span[class^="TypographyStyle"] {
                        color: ${getColor("text.link")};
                        font-size: 14px;
                        font-weight: ${TYPOGRAPHY.breadcrumbs.weight};
                    }
                    span[aria-current="page"] {
                        color: ${getColor("text.accent")};
                        font-weight: ${TYPOGRAPHY.breadcrumbs.current.weight};
                        font-size: 14px;
                        opacity: 1;
                    }

                    > a > span[class^="TypographyStyle"] {
                        color: ${getColor("text.link")};
                        opacity: 1;
                        font-size: 14px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                padding: 0;
                                padding-bottom: 15px;
                            `,
                        ],
                        "max",
                    )}
            }
            ~ main[class^="PageStyle"] {
                min-height: 500px;
                padding: 15px 0;
                main {
                    padding-left: 0 !important;
                    padding-right: 0 !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                padding: 15px;
                                padding-top: 0px;
                            `,
                            css`
                                padding: 15px;
                                padding-top: 0px;
                            `,
                        ],
                        "max",
                    )}
            }
            ~ main[data-test-selector="homePage"] {
                padding: 0;
            }
        `,
    },
    container: {
        css: css`
            background-color: #ffffff;
            display: flex;
            @media (min-width: 992px) and (max-width: 1166px) {
                border-bottom: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        border-bottom: none;
                    `,
                    css`
                        border-bottom: none;
                    `,
                    css`
                        border-bottom: none;
                    `,
                    null,
                    null,
                ])}
            ${isThemeActive(THEME_IDS.MGBW)
                ? `
                @media (min-width: 1167px) {
                    border-bottom: 1px solid #E8E8E8;
                }
            `
                : ""}
        `,
    },
    mobileSearchBar: {
        css: css`
            max-width: calc(100% - 40px);
            margin: 0 auto;
            visibility: hidden;
            opacity: 0;
            @media (min-width: 992px) and (max-width: 1166px) {
                display: block !important;
            }
            ${isThemeActive(THEME_IDS.MGBW)
                ? `
                @media (min-width: 768px) {
                    display: block !important;
                }
                @media (min-width: 1167px) {
                    max-width: 100%;
                    #mobileSearchBar {
                        max-width: 800px;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }
                }
            `
                : ""}
        `,
    },
    searchInteractionBlocker: {
        css: css`
            display: none;
            @media (min-width: 1167px) {
                display: block;
                position: absolute;
                inset: 0;
                z-index: 2071;
            }
        `,
    },
    brandLogo: {
        css: css`
            max-width: calc(100% - 40px);
            margin: 0 auto;
            border-bottom: 1px solid #e0e0e0;
            @media (min-width: 992px) and (max-width: 1166px) {
                display: block !important;
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header: React.FC<Props> = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    surHeaderHeight,
}) => {
    const [offset1, setOffset1] = useState(0);
    const [scrolled, setScrolled] = useState(false);
    const [mobileSearchState, dispatchMobileSearch] = useReducer(mobileSearchReducer, initialMobileSearchState);
    const mobileSearchVisibleRef = React.useRef(mobileSearchState.isVisible);

    const dynamicMobileSearchBarStyles = {
        ...styles.mobileSearchBar,
        css: css`
            ${styles.mobileSearchBar?.css}
            visibility: ${mobileSearchState.isVisible ? "visible" : "hidden"};
            opacity: ${mobileSearchState.isVisible ? "1" : "0"};
            max-height: ${mobileSearchState.isVisible ? "100px" : "0"};
            overflow: hidden;
            transition: all 0.3s ease-in-out;
            ${isThemeActive(THEME_IDS.MGBW)
                ? `
                @media (min-width: 1167px) {
                    overflow: visible;
                    max-height: ${mobileSearchState.isVisible ? "none" : "0"};
                    position: absolute;
                    left: 0;
                    right: 0;
                    top: 100%;
                    padding: ${mobileSearchState.isVisible ? "40px 0" : "0"};
                    box-shadow: ${mobileSearchState.isVisible ? "0px 4px 12px 0px rgba(13, 10, 44, 0.06)" : "none"};
                    background: #fdfdfd;
                    z-index: 2072;
                }
            `
                : ""}
        `,
    };

    const toggleMobileSearch = () => {
        dispatchMobileSearch({ type: "TOGGLE_VISIBILITY" });
    };

    const showMobileSearch = () => {
        dispatchMobileSearch({ type: "SHOW" });
    };

    const hideMobileSearch = () => {
        // Clear the search input text before closing
        const searchInput = document.querySelector(
            '#mobileSearchBar input[type="text"], #mobileSearchBar input.search-input',
        ) as HTMLInputElement;
        if (searchInput && searchInput.value) {
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                "value",
            )?.set;
            if (nativeInputValueSetter) {
                nativeInputValueSetter.call(searchInput, "");
                searchInput.dispatchEvent(new Event("input", { bubbles: true }));
            }
        }
        dispatchMobileSearch({ type: "HIDE" });
    };

    const focusSearchInput = () => {
        setTimeout(() => {
            const mobileSearchInput = document.querySelector("#mobileSearchBar .search-input") as HTMLInputElement;
            if (mobileSearchInput) {
                mobileSearchInput.focus();
            }
        }, 300);
    };

    const mobileSearchContextValue: MobileSearchContextType = {
        isVisible: mobileSearchState.isVisible,
        toggleMobileSearch,
        showMobileSearch,
        hideMobileSearch,
        focusSearchInput,
    };

    useEffect(() => {
        const handleScroll = () => {
            const check = window.parent.document.getElementById("siteIFrame");
            if (!check) {
                const scrollPosition = window.pageYOffset;
                setOffset1(scrollPosition);
                if (scrollPosition > 0) {
                    setScrolled(true);
                } else {
                    setScrolled(false);
                }
            } else {
                setOffset1(2000);
                setScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll); // Clean up
    }, []);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        const closeMobileSearch = () => {
            dispatchMobileSearch({ type: "HIDE" });
            mobileSearchVisibleRef.current = false;
            (document.activeElement as HTMLElement | null)?.blur?.();
        };

        window.addEventListener("popstate", closeMobileSearch);
        window.addEventListener("hashchange", closeMobileSearch);

        const patch = (method: "pushState" | "replaceState") => {
            const h = window.history as History;
            const orig = h[method];
            h[method] = function (this: History, ...args: any[]) {
                const res = orig.apply(this, args as any);
                closeMobileSearch();
                return res;
            };
            return () => {
                h[method] = orig;
            };
        };

        const restorePush = patch("pushState");
        const restoreReplace = patch("replaceState");

        return () => {
            window.removeEventListener("popstate", closeMobileSearch);
            window.removeEventListener("hashchange", closeMobileSearch);
            restorePush();
            restoreReplace();
        };
    }, []);

    useEffect(() => {
        if (!isThemeActive(THEME_IDS.MGBW) || typeof window === "undefined" || window.innerWidth < 1167) {
            return;
        }
        const overlay = document.getElementById("mgbw-search-overlay");
        if (mobileSearchState.isVisible && !overlay) {
            const newOverlay = document.createElement("div");
            newOverlay.id = "mgbw-search-overlay";
            newOverlay.style.cssText =
                "position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,0.2);z-index:1250;cursor:default;";
            newOverlay.addEventListener("click", hideMobileSearch);
            document.body.appendChild(newOverlay);
        } else if (!mobileSearchState.isVisible && overlay) {
            overlay.remove();
        }
        return () => {
            const el = document.getElementById("mgbw-search-overlay");
            if (el) {
                el.remove();
            }
        };
    }, [mobileSearchState.isVisible]);

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <MobileSearchContext.Provider value={mobileSearchContextValue}>
            <ThemeFontLoader />
            {/* {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>} */}
            <Page
                as="header"
                {...styles.page}
                fullWidth={[true, true, true, true, true]}
                {...styles.headerWrapper}
                id="MenuBAr"
                isHeader={true}
                style={scrolled ? { opacity: 0.985 } : { opacity: 1 }}
            >
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <div id="secondaryNavRef">
                        <Zone contentId={id} zoneName="SecondaryNavigation" />
                    </div>
                    <StyledWrapper {...styles.container}>
                        <Zone contentId={id} zoneName="MainNavigation" />
                    </StyledWrapper>
                    {isThemeActive(THEME_IDS.MGBW) && mobileSearchState.isVisible && (
                        <StyledWrapper {...styles.searchInteractionBlocker} onClick={hideMobileSearch} />
                    )}
                    <Hidden above="md" {...dynamicMobileSearchBarStyles}>
                        <div id="mobileSearchBar" style={{ position: "relative" }}>
                            {isThemeActive(THEME_IDS.MGBW) && (
                                <Icon
                                    src={MGBWSearchIcon}
                                    size={18}
                                    color="#333333"
                                    css={css`
                                        display: block;
                                        position: absolute;
                                        left: 0;
                                        top: calc(50% - 0px);
                                        transform: translateY(-50%);
                                        width: 18px !important;
                                        height: 18px !important;
                                        pointer-events: none;
                                        z-index: 1;
                                        svg {
                                            width: 18px !important;
                                            height: 18px !important;
                                        }
                                        @media (min-width: 1167px) {
                                            position: static;
                                            transform: none;
                                            flex-shrink: 0;
                                            width: 20px !important;
                                            height: 20px !important;
                                            aspect-ratio: 1/1;
                                            svg {
                                                width: 20px !important;
                                                height: 20px !important;
                                            }
                                        }
                                    `}
                                />
                            )}
                            <Zone contentId={id} zoneName="mobileSearchBar" />
                            {isThemeActive(THEME_IDS.MGBW) && (
                                <Icon
                                    src={MGBWCloseIconSmall}
                                    size={16}
                                    color="#333333"
                                    css={css`
                                        display: block;
                                        position: absolute;
                                        right: 0;
                                        top: calc(50% - 0px);
                                        transform: translateY(-50%);
                                        width: 16px !important;
                                        height: 16px !important;
                                        cursor: pointer;
                                        z-index: 1;
                                        svg {
                                            width: 16px !important;
                                            height: 16px !important;
                                        }
                                        @media (min-width: 1167px) {
                                            position: static;
                                            transform: none;
                                            flex-shrink: 0;
                                            width: 20px !important;
                                            height: 20px !important;
                                            aspect-ratio: 1/1;
                                            svg {
                                                width: 20px !important;
                                                height: 20px !important;
                                            }
                                        }
                                    `}
                                    onClick={() => hideMobileSearch()}
                                />
                            )}
                        </div>
                    </Hidden>
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </MobileSearchContext.Provider>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
